import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { StatsSkeleton, CardSkeleton, Skeleton } from '../components/ui/skeleton';
import NavigationMenu from '../components/NavigationMenu';
import UserMenu from '../components/UserMenu';
import Footer from '../components/Footer';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { 
  Gift, Copy, Share2, Users, Check, Crown, Moon, Sun, 
  Sparkles, ChevronRight, Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ReferralPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user, token, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    if (!user) {
      navigate('/auth?mode=signin&redirect=/referral');
      return;
    }
    
    const fetchReferralData = async () => {
      try {
        const [statsRes, historyRes] = await Promise.all([
          axios.get(`${API}/api/referral/stats`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/api/referral/history`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setStats(statsRes.data);
        setHistory(historyRes.data.referrals || []);
      } catch (error) {
        console.error('Failed to fetch referral data:', error);
        toast.error('Failed to load referral data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchReferralData();
  }, [user, authLoading, navigate, token]);

  const copyCode = () => {
    if (stats?.referral_code) {
      navigator.clipboard.writeText(stats.referral_code);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const copyLink = () => {
    if (stats?.referral_link) {
      navigator.clipboard.writeText(stats.referral_link);
      toast.success('Referral link copied!');
    }
  };

  const shareLink = async () => {
    if (stats?.referral_link && navigator.share) {
      try {
        await navigator.share({
          title: 'Try FlipValue!',
          text: 'Use my referral code to get 1 free month of FlipValue Pro!',
          url: stats.referral_link
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyLink();
        }
      }
    } else {
      copyLink();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        {/* Header */}
        <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Skeleton className="w-8 h-8 rounded-lg" />
              <Skeleton className="w-24 h-6" />
            </div>
            <Skeleton className="w-8 h-8 rounded-full" />
          </div>
        </header>
        
        <main className="flex-1 max-w-4xl mx-auto px-4 py-6 w-full space-y-6">
          <div className="text-center space-y-2">
            <Skeleton className="w-32 h-6 mx-auto rounded-full" />
            <Skeleton className="w-64 h-8 mx-auto" />
            <Skeleton className="w-48 h-4 mx-auto" />
          </div>
          
          <StatsSkeleton count={4} />
          
          <CardSkeleton />
          <CardSkeleton />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <NavigationMenu />
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Zap className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-bold text-lg">FlipValue</span>
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-4 py-6 w-full">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            Referral Program
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">
            Refer Friends & Earn Free Pro Time!
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base max-w-xl mx-auto">
            Share FlipValue with friends and both of you get rewarded
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-card border border-border rounded-xl p-4 text-center">
            <Users className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats?.successful_referrals || 0}</p>
            <p className="text-xs text-muted-foreground">Successful</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 text-center">
            <Gift className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats?.pending_referrals || 0}</p>
            <p className="text-xs text-muted-foreground">Pending</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 text-center">
            <Crown className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats?.total_free_months_earned || 0}</p>
            <p className="text-xs text-muted-foreground">Free Months</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 text-center">
            <Sparkles className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-2xl font-bold">{5 - (stats?.successful_referrals || 0)}</p>
            <p className="text-xs text-muted-foreground">Until 1 Year Free</p>
          </div>
        </div>

        {/* Referral Code Card */}
        <div className="bg-card border border-border rounded-xl p-5 mb-6">
          <h2 className="font-semibold mb-4">Your Referral Code</h2>
          
          <div className="flex gap-2 mb-4">
            <div className="flex-1 relative">
              <Input 
                value={stats?.referral_code || ''}
                readOnly
                className="text-center font-mono text-lg font-bold tracking-wider pr-10"
              />
              <Button 
                variant="ghost" 
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2"
                onClick={copyCode}
              >
                {copied ? <Check className="w-4 h-4 text-primary" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <div className="flex gap-2">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={copyLink}
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Link
            </Button>
            <Button 
              className="flex-1 bg-primary hover:bg-primary/90"
              onClick={shareLink}
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>

          {stats?.referral_link && (
            <p className="text-xs text-muted-foreground mt-3 break-all">
              {stats.referral_link}
            </p>
          )}
        </div>

        {/* How It Works */}
        <div className="bg-card border border-border rounded-xl p-5 mb-6">
          <h2 className="font-semibold mb-4">How It Works</h2>
          
          <div className="space-y-4 text-sm">
            <p className="text-muted-foreground">
              Refer friends and earn free Pro time!
            </p>
            
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="p-1 bg-primary/10 rounded-full mt-0.5">
                  <Check className="w-3 h-3 text-primary" />
                </div>
                <span>Share your unique link or code.</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="p-1 bg-primary/10 rounded-full mt-0.5">
                  <Check className="w-3 h-3 text-primary" />
                </div>
                <div>
                  <span>When someone signs up for Basic or Pro using your code and becomes a paying member:</span>
                  <ul className="mt-2 ml-4 space-y-1 text-muted-foreground">
                    <li>• They get 1 free month.</li>
                    <li>• You get 1 free month of Pro for each successful referral (up to 4 referrals).</li>
                  </ul>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="p-1 bg-primary/10 rounded-full mt-0.5">
                  <Check className="w-3 h-3 text-primary" />
                </div>
                <span>Reach 5 successful referrals → You unlock <strong className="text-primary">1 full year of Pro free!</strong></span>
              </li>
            </ul>

            <div className="mt-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
              <p className="text-primary font-medium text-center">
                Maximum reward: 16 free months of Pro
              </p>
            </div>
          </div>
        </div>

        {/* Progress to Year Free */}
        {stats?.successful_referrals < 5 && (
          <div className="bg-card border border-border rounded-xl p-5 mb-6">
            <h2 className="font-semibold mb-3">Progress to 1 Year Free</h2>
            <div className="flex items-center gap-2 mb-2">
              {[1, 2, 3, 4, 5].map((num) => (
                <div 
                  key={num}
                  className={`flex-1 h-3 rounded-full ${
                    num <= (stats?.successful_referrals || 0)
                      ? 'bg-primary'
                      : 'bg-secondary'
                  }`}
                />
              ))}
            </div>
            <p className="text-sm text-muted-foreground text-center">
              {stats?.successful_referrals || 0} of 5 referrals completed
            </p>
          </div>
        )}

        {/* Referral History */}
        {history.length > 0 && (
          <div className="bg-card border border-border rounded-xl p-5">
            <h2 className="font-semibold mb-4">Referral History</h2>
            <div className="space-y-3">
              {history.map((ref, idx) => (
                <div 
                  key={ref.id || idx}
                  className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${
                      ref.status === 'rewarded' ? 'bg-primary/10' : 
                      ref.status === 'qualified' ? 'bg-green-500/10' : 
                      'bg-yellow-500/10'
                    }`}>
                      <Users className={`w-4 h-4 ${
                        ref.status === 'rewarded' ? 'text-primary' : 
                        ref.status === 'qualified' ? 'text-green-500' : 
                        'text-yellow-500'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{ref.referred_email}</p>
                      <p className="text-xs text-muted-foreground">
                        {ref.plan && `${ref.plan} plan • `}
                        {new Date(ref.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      ref.status === 'rewarded' ? 'bg-primary/10 text-primary' : 
                      ref.status === 'qualified' ? 'bg-green-500/10 text-green-500' : 
                      'bg-yellow-500/10 text-yellow-500'
                    }`}>
                      {ref.status === 'rewarded' ? 'Earned' : 
                       ref.status === 'qualified' ? 'Qualified' : 
                       'Pending'}
                    </span>
                    {ref.reward_earned && (
                      <p className="text-xs text-primary mt-1">{ref.reward_earned}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-8 text-center">
          <Button 
            variant="outline"
            onClick={() => navigate('/pricing')}
          >
            View Plans
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </main>

      <Footer />
    </div>
  );
}
